import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {AdminDashboardReportBlock, AdminDashboardReportProvider, AdminDashboardReportRow} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {adbCategoryAxis, adbChartTheme, adbGrid, adbLegend, adbRedrawOnThemeChange, adbTooltip} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {
  AdminDashboardFailingTopic,
  AdminDashboardFailingTopics,
  AdminDashboardQueryParams,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';

/** The ECharts entry point behind a plain object, so a spec can stub it (`spyOn(echarts, 'init')` throws). */
export const failingTopicsCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/** The mockup draws the six topics with the most unresolved questions (the API sends at most six too). */
const TOP_TOPICS = 6;

/**
 * Product quality: the topics with the most unresolved XatBot questions, each as a pair of bars (previous period faint,
 * this period strong). Reloads whenever the dashboard's params change; the chart is created only when a topic has an
 * unresolved question this period.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-failing-topics',
  templateUrl: './admin-dashboard-failing-topics.component.html',
  styleUrls: ['./admin-dashboard-failing-topics.component.scss']
})
export class AdminDashboardFailingTopicsComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() params: AdminDashboardQueryParams;
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  topics: AdminDashboardFailingTopic[] = [];
  riser: AdminDashboardFailingTopic | null = null;
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;
  readonly failedTitle = 'Couldn\'t load top failing topics';

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry,
    private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('product-quality', this.host.nativeElement, this);
  }

  @ViewChild('chart') set chartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = failingTopicsCharts.init(this.chartElement);
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.disposeChart();
    this.reportRegistry.unregister(this);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-11). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    const title = 'Top failing topics';
    if (this.failed) {
      return [{kind: 'empty', title, text: this.failedTitle}];
    }
    const subtitle = this.riser
      ? `Unresolved questions by topic. ${this.riser.label} is up ${this.riserChange} on the previous ${this.periodLong}.`
      : 'Unresolved questions by topic, against the previous period.';
    if (!this.hasUnresolved) {
      return [{kind: 'empty', title, subtitle, text: 'No unresolved questions in this view'}];
    }
    return [
      {
        kind: 'chart',
        title,
        subtitle,
        image: this.chart ? this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF'}) : null,
        widthPx: this.chart?.getWidth() ?? 0,
        heightPx: this.chart?.getHeight() ?? 0
      },
      {
        // The numbers behind the bars (on screen: the bar labels and the tooltip), raw like the card prints them.
        kind: 'table',
        title: 'Unresolved questions by topic',
        columns: [{header: 'Topic', weight: 3}, {header: 'This period', align: 'right'}, {header: 'Previous period', align: 'right'}],
        rows: this.topics.map((topic): AdminDashboardReportRow => ({cells: [topic.label, String(topic.current), String(topic.previous)]}))
      }
    ];
  }

  /** True when at least one topic has an unresolved question this period. */
  get hasUnresolved(): boolean {
    return this.topics.some(topic => topic.current > 0);
  }

  /** "30 days" for '30d', "12 months" for '12m'. */
  get periodLong(): string {
    const long: {[key: string]: string} = {'7d': '7 days', '30d': '30 days', '90d': '90 days', '12m': '12 months'};
    return long[this.params?.period ?? '30d'] ?? '30 days';
  }

  /** "42.9%": how much the riser grew against the previous period. */
  get riserChange(): string {
    const riser = this.riser;
    return riser && riser.previous ? `${(((riser.current - riser.previous) / riser.previous) * 100).toFixed(1)}%` : '';
  }

  /** "30 unresolved · 21 in the previous period" */
  tooltipText(topic: AdminDashboardFailingTopic): string {
    return `${topic?.current ?? 0} unresolved · ${topic?.previous ?? 0} in the previous period`;
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getFailingTopics(this.params).subscribe({
      next: result => this.show(result, false),
      error: () => this.show(null, true)
    });
  }

  private show(result: AdminDashboardFailingTopics | null, failed: boolean): void {
    this.topics = (result?.Topics ?? []).slice(0, TOP_TOPICS);
    this.riser = result?.Riser ?? null;
    this.failed = failed;
    this.loaded = true;
    this.render();
    this.report(failed ? 'failed' : this.hasUnresolved ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  /**
   * The OCA-E-159 topics chart (M/charts.ts topicsOption): this period in orange with its count on the right, the
   * previous period in the line colour, names truncated at 170 px, the legend top right.
   */
  private render(): void {
    if (!this.chart) {
      return;
    }
    const topics = this.topics;
    const t = adbChartTheme(this.host.nativeElement);
    const option: echarts.EChartsOption = {
      grid: adbGrid({left: 16, right: 40, top: 26, bottom: 4}),
      legend: {...adbLegend(t), data: ['This period', 'Previous period']},
      xAxis: {type: 'value', show: false},
      yAxis: adbCategoryAxis(t, topics.map(topic => topic.label), {
        inverse: true,
        axisLine: {show: false},
        axisLabel: {color: t.ink, fontSize: 11, width: 170, overflow: 'truncate'}
      }),
      series: [
        {
          name: 'Previous period',
          type: 'bar',
          data: topics.map(topic => topic.previous),
          barMaxWidth: 8,
          barGap: '20%',
          itemStyle: {color: t.line, borderRadius: 2}
        },
        {
          name: 'This period',
          type: 'bar',
          data: topics.map(topic => topic.current),
          barMaxWidth: 8,
          itemStyle: {color: t.orange, borderRadius: 2},
          label: {show: true, position: 'right', color: t.ink2, fontSize: 10}
        }
      ],
      tooltip: adbTooltip(t, {
        axisPointer: {type: 'shadow'},
        formatter: (params: any) => {
          const point = Array.isArray(params) ? params[0] : params;
          return `<b>${point?.name}</b><br/>${this.tooltipText(topics[point?.dataIndex])}`;
        }
      })
    };
    this.chart.setOption(option, true);
  }
}
